import { Controller, useForm } from 'react-hook-form';

import { TrackableList, UpdateTrackableList } from 'types/trackables';

import { useUpdateTrackableList } from '../hooks/useUpdateTrackableList';
import { TRACKABLE_OBJECT_TYPE_OPTIONS } from '../utils';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Select } from 'components/Select/Select';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';

export interface EditTrackableListFormProps {
  trackableList: TrackableList;
  onSuccess?: () => void;
}

export function EditTrackableListForm({
  trackableList,
  onSuccess,
}: EditTrackableListFormProps) {
  const {
    formState,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateTrackableList>({
    defaultValues: {
      name: trackableList.name,
      description: trackableList.description || '',
      object_type: trackableList.object_type,
    },
  });

  const {
    mutate: updateTrackableList,
    isLoading,
    isSuccess,
  } = useUpdateTrackableList({
    onSuccess: () => {
      onSuccess?.();
    },
  });

  const onSubmit = (data: UpdateTrackableList) => {
    updateTrackableList({
      id: trackableList.id,
      data,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <Field label="List name" required error={errors.name?.message}>
            <Input
              {...register('name', {
                required: 'Please enter a name for the trackable list.',
              })}
              placeholder="Enter list name..."
            />
          </Field>

          <Field label="Description">
            <Input
              {...register('description')}
              placeholder="Enter description (optional)..."
            />
          </Field>

          <Field
            label="Object Type"
            required
            info="Changing the object type may affect existing watchlist items in this list."
          >
            <Controller
              name="object_type"
              control={control}
              rules={{ required: 'Please select an object type.' }}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={TRACKABLE_OBJECT_TYPE_OPTIONS.find(
                    (opt) => opt.value === value
                  )}
                  onChange={(option) => onChange(option?.value)}
                  options={TRACKABLE_OBJECT_TYPE_OPTIONS}
                  placeholder="Select object type"
                />
              )}
            />
          </Field>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <ButtonGroup>
            <Button
              type="submit"
              size="small"
              variant="primary"
              loading={isLoading}
            >
              Save Changes
            </Button>
            {formState.isDirty && (
              <InlineNotification>Unsaved changes</InlineNotification>
            )}

            {isSuccess && !formState.isDirty && (
              <InlineNotification intent="success">Saved!</InlineNotification>
            )}
          </ButtonGroup>
        </div>
      </form>
    </>
  );
}
